import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import DigtekNavbar from "@/components/digtek/DigtekNavbar";
import DigtekFooter from "@/components/digtek/DigtekFooter";
import { findProject, projects } from "@/data/projects";

export default function CaseStudy() {
    const { slug } = useParams();
    const project = findProject(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        if (project) document.title = `${project.title} — Aximo Studios`;
        return () => {
            document.title = "Aximo Studios — Full-Service Creative Agency";
        };
    }, [project]);

    if (!project) return <Navigate to="/" replace />;

    const idx = projects.findIndex((p) => p.slug === slug);
    const next = projects[(idx + 1) % projects.length];

    return (
        <main
            data-testid="case-study-page"
            className="digtek-page min-h-screen bg-ink text-bone"
        >
            <DigtekNavbar />

            {/* Hero */}
            <section className="relative pt-32 pb-16 lg:pt-44 lg:pb-24 grain">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ember mb-8">
                        <span className="block w-8 h-px bg-ember" />
                        Case study · {project.year}
                    </div>
                    <h1
                        data-testid="case-study-title"
                        className="font-display font-medium tracking-tightest text-bone text-6xl sm:text-7xl lg:text-[124px] leading-[0.92] max-w-[14ch]"
                    >
                        {project.title}
                    </h1>
                    <p className="mt-6 text-bone-300 text-lg max-w-[60ch]">
                        {project.subtitle}
                    </p>

                    {/* Meta strip */}
                    <dl className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {[
                            { k: "Client", v: project.client },
                            { k: "Sector", v: project.sector },
                            { k: "Duration", v: project.duration },
                            { k: "Services", v: project.services.join(" · ") },
                        ].map((m) => (
                            <div key={m.k} className="bg-ink-800 p-5">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                    {m.k}
                                </div>
                                <div className="font-display text-bone text-base tracking-tight">
                                    {m.v}
                                </div>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            {/* Hero image */}
            <section className="relative">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="overflow-hidden border border-white/10 hover-zoom h-[420px] lg:h-[640px]">
                        <img
                            src={project.image}
                            alt={project.title}
                            fetchPriority="high"
                            decoding="async"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Intro + body */}
            <section className="py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-4">
                            Brief
                        </div>
                        <h2 className="font-display text-bone text-3xl lg:text-4xl tracking-tight leading-[1.1] max-w-[18ch]">
                            {project.intro}
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-bone-300 text-base lg:text-lg leading-relaxed">
                        <div>
                            <h3 className="font-display text-bone text-xl mb-3">
                                The challenge
                            </h3>
                            <p>{project.challenge}</p>
                        </div>
                        <div>
                            <h3 className="font-display text-bone text-xl mb-3">
                                Our approach
                            </h3>
                            <p>{project.approach}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section className="py-12 lg:py-16">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                    {project.gallery.map((src, i) => {
                        let sizeClass;
                        if (i === 0) sizeClass = "lg:col-span-12 h-[360px] lg:h-[600px]";
                        else if (i % 2 === 1) sizeClass = "lg:col-span-7 h-[300px] lg:h-[460px]";
                        else sizeClass = "lg:col-span-5 h-[300px] lg:h-[460px]";
                        return (
                            <div
                                key={src}
                                className={`overflow-hidden border border-white/10 hover-zoom ${sizeClass}`}
                            >
                                <img
                                    src={src}
                                    alt={`${project.title} ${i + 1}`}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Outcomes */}
            <section className="py-20 lg:py-28 bg-ink-900 border-y border-white/10 grain">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-8">
                        Outcomes
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 border-t border-white/10">
                        {project.outcomes.map((o) => (
                            <div
                                key={o.label}
                                className="p-8 lg:p-10 border-b md:border-b-0 md:border-r last:border-r-0 border-white/10"
                            >
                                <div className="font-display text-ember text-5xl lg:text-7xl tracking-tightest leading-none mb-4">
                                    {o.stat}
                                </div>
                                <div className="text-bone-300 text-sm uppercase tracking-[0.18em]">
                                    {o.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote */}
            {project.quote && (
                <section className="py-24 lg:py-36">
                    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                        <blockquote className="font-display text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-balance">
                            <span className="text-ember font-serif-i italic">
                                “
                            </span>
                            {project.quote.text}
                            <span className="text-ember font-serif-i italic">
                                ”
                            </span>
                        </blockquote>
                        <footer className="mt-8 text-sm text-bone-300 flex items-center gap-3">
                            <span className="block w-8 h-px bg-ember" />
                            <span className="font-display text-bone">
                                {project.quote.author}
                            </span>
                        </footer>
                    </div>
                </section>
            )}

            {/* Next + CTA */}
            <section className="py-20 lg:py-28 bg-ink-900 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <Link
                        to={`/work/${next.slug}`}
                        data-testid="case-study-next"
                        className="group block"
                    >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3 flex items-center gap-2">
                            Next case study
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                        </div>
                        <div className="font-display text-bone text-4xl lg:text-5xl tracking-tight group-hover:text-ember transition-colors">
                            {next.title}
                        </div>
                        <div className="text-bone-300 text-sm mt-2">
                            {next.category} · {next.year}
                        </div>
                    </Link>

                    <Link
                        to="/#contact"
                        data-testid="case-study-cta"
                        className="lg:justify-self-end inline-flex items-center gap-2 btn-ember h-14 px-7 text-sm font-medium tracking-wide"
                    >
                        Start a project like this
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <DigtekFooter />
        </main>
    );
}
